import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { formatCurrency, validateEmail, validatePhone } from '@/lib/utils';
import { calculateRoute, calculatePricingTiers, geocodeAddress, getPlacePredictions } from '@/services/googleMaps';
import type { Student, QuoteRequest, PricingTier, RouteCalculation } from '@/types';

type SemesterType = 'full' | 'fall' | 'spring' | 'custom';

interface FormData {
  guardianName: string;
  guardianEmail: string;
  guardianPhone: string;
  pickupAddress: string;
  dropoffAddress: string;
  students: Student[];
  semesterType: SemesterType;
  customDays: number;
  daysPerWeek: number;
}

const SEMESTER_CONFIG = {
  full: { weeks: 36, label: 'Full Year', description: 'September - May (36 weeks)' },
  fall: { weeks: 18, label: 'Fall Semester', description: 'September - December (18 weeks)' },
  spring: { weeks: 18, label: 'Spring Semester', description: 'January - May (18 weeks)' },
  custom: { weeks: 0, label: 'Custom Schedule', description: 'Choose your own schedule' },
};

export default function YearlyQuote() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();

  const [formData, setFormData] = useState<FormData>({
    guardianName: '',
    guardianEmail: user?.email || '',
    guardianPhone: '',
    pickupAddress: '',
    dropoffAddress: '',
    students: [{ id: '1', firstName: '', lastName: '' }],
    semesterType: 'full',
    customDays: 90,
    daysPerWeek: 5,
  });

  const [routeCalculation, setRouteCalculation] = useState<RouteCalculation | null>(null);
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Address autocomplete state
  const [pickupSuggestions, setPickupSuggestions] = useState<Array<{ description: string; place_id: string }>>([]);
  const [dropoffSuggestions, setDropoffSuggestions] = useState<Array<{ description: string; place_id: string }>>([]);
  const [showPickupSuggestions, setShowPickupSuggestions] = useState(false);
  const [showDropoffSuggestions, setShowDropoffSuggestions] = useState(false);

  const getTotalDays = (): number => {
    if (formData.semesterType === 'custom') {
      return formData.customDays;
    }
    return SEMESTER_CONFIG[formData.semesterType].weeks * formData.daysPerWeek;
  };

  const getTotalWeeks = (): number => {
    if (formData.semesterType === 'custom') {
      return Math.ceil(formData.customDays / formData.daysPerWeek);
    }
    return SEMESTER_CONFIG[formData.semesterType].weeks;
  };

  const handleAddressSearch = async (address: string, type: 'pickup' | 'dropoff') => {
    if (address.length < 3) {
      if (type === 'pickup') setPickupSuggestions([]);
      else setDropoffSuggestions([]);
      return;
    }

    try {
      const predictions = await getPlacePredictions(address);
      if (type === 'pickup') {
        setPickupSuggestions(predictions);
        setShowPickupSuggestions(true);
      } else {
        setDropoffSuggestions(predictions);
        setShowDropoffSuggestions(true);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const selectAddress = (address: string, type: 'pickup' | 'dropoff') => {
    if (type === 'pickup') {
      setFormData(prev => ({ ...prev, pickupAddress: address }));
      setShowPickupSuggestions(false);
    } else {
      setFormData(prev => ({ ...prev, dropoffAddress: address }));
      setShowDropoffSuggestions(false);
    }
    // Reset route calculation when address changes
    setRouteCalculation(null);
    setPricingTiers([]);
  };

  const calculateRouteAndPricing = async () => {
    if (!formData.pickupAddress || !formData.dropoffAddress) {
      toast.error('Please enter both pickup and dropoff addresses');
      return;
    }

    setIsCalculating(true);

    try {
      // Calculate route directly from addresses
      const route = await calculateRoute(
        formData.pickupAddress,
        formData.dropoffAddress
      );

      setRouteCalculation(route);

      // Calculate pricing tiers
      const tiers = calculatePricingTiers(route);
      setPricingTiers(tiers);

      toast.success('Route calculated successfully!');
    } catch (error) {
      console.error('Error calculating route:', error);
      toast.error('Error calculating route. Please try again.');
    } finally {
      setIsCalculating(false);
    }
  };

  const addStudent = () => {
    if (formData.students.length < 6) {
      setFormData(prev => ({
        ...prev,
        students: [...prev.students, { id: String(prev.students.length + 1), firstName: '', lastName: '' }],
      }));
    }
  };

  const removeStudent = (index: number) => {
    if (formData.students.length > 1) {
      setFormData(prev => ({
        ...prev,
        students: prev.students.filter((_, i) => i !== index),
      }));
    }
  };

  const updateStudent = (index: number, field: 'firstName' | 'lastName', value: string) => {
    setFormData(prev => ({
      ...prev,
      students: prev.students.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.guardianName.trim()) {
      newErrors.guardianName = 'Name is required';
    }

    if (!validateEmail(formData.guardianEmail)) {
      newErrors.guardianEmail = 'Valid email is required';
    }

    if (!validatePhone(formData.guardianPhone)) {
      newErrors.guardianPhone = 'Valid phone number is required';
    }

    if (!formData.pickupAddress.trim()) {
      newErrors.pickupAddress = 'Pickup address is required';
    }

    if (!formData.dropoffAddress.trim()) {
      newErrors.dropoffAddress = 'School address is required';
    }

    const incompleteStudents = formData.students.some(s => !s.firstName.trim() || !s.lastName.trim());
    if (incompleteStudents) {
      newErrors.students = 'All students must have first and last names';
    }

    if (!routeCalculation) {
      newErrors.route = 'Please calculate your route first';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getSelectedTier = (): PricingTier | null => {
    const studentCount = formData.students.length;
    return pricingTiers.find(t => t.students === studentCount) || null;
  };

  const calculateTotal = (): number => {
    const tier = getSelectedTier();
    if (!tier) return 0;
    return tier.pricePerWeek * getTotalWeeks();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors below');
      return;
    }

    if (!user) {
      toast.error('Please sign in to submit a quote');
      return;
    }

    const tier = getSelectedTier();
    if (!tier) {
      toast.error('Please calculate pricing first');
      return;
    }

    setIsSubmitting(true);

    try {
      const quoteRequest: Omit<QuoteRequest, 'id'> = {
        userId: user.uid,
        type: 'yearly',
        status: 'pending',
        guardianName: formData.guardianName,
        guardianEmail: formData.guardianEmail,
        guardianPhone: formData.guardianPhone,
        pickupAddress: formData.pickupAddress,
        pickupZipcode: '',
        dropoffAddress: formData.dropoffAddress,
        dropoffZipcode: '',
        students: formData.students,
        semesterType: formData.semesterType,
        selectedDays: getTotalDays(),
        calculatedPrice: calculateTotal(),
        priceBreakdown: {
          basePrice: tier.pricePerTrip,
          perStudentPrice: tier.pricePerTrip,
          numberOfStudents: formData.students.length,
          numberOfSessions: getTotalWeeks(),
          sessionPrice: tier.pricePerWeek,
          subtotal: calculateTotal(),
          total: calculateTotal(),
        },
        routeDistance: routeCalculation!.distanceMiles,
        routeDuration: routeCalculation!.durationMinutes,
        routeName: `${formData.pickupAddress.split(',')[0]} → ${formData.dropoffAddress.split(',')[0]}`,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await addDoc(collection(db, 'quoteRequests'), {
        ...quoteRequest,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setSubmitted(true);
      toast.success('Quote request submitted successfully!');
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Failed to submit quote. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <Card className="max-w-lg w-full text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Quote Request Submitted!</h2>
            <p className="text-gray-600 mb-6">
              We've received your school year transportation request.
              Check your email at <strong>{formData.guardianEmail}</strong> for your detailed quote.
            </p>
            <div className="bg-shuttlebee-yellow/10 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700">
                <strong>Estimated Total:</strong> {formatCurrency(calculateTotal())} for {getTotalWeeks()} weeks
              </p>
            </div>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate('/dashboard')}>View Dashboard</Button>
              <Button variant="outline" onClick={() => navigate('/')}>Back to Home</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const selectedTier = getSelectedTier();

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            School Year Transportation
          </h1>
          <p className="text-gray-600">
            Daily transportation to and from school with a custom route
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Guardian Info */}
              <Card>
                <CardHeader>
                  <CardTitle>Parent/Guardian Information</CardTitle>
                  <CardDescription>Primary contact for this registration</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Input
                    label="Full Name"
                    value={formData.guardianName}
                    onChange={(e) => setFormData(prev => ({ ...prev, guardianName: e.target.value }))}
                    error={errors.guardianName}
                    placeholder="John Smith"
                  />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input
                      label="Email"
                      type="email"
                      value={formData.guardianEmail}
                      onChange={(e) => setFormData(prev => ({ ...prev, guardianEmail: e.target.value }))}
                      error={errors.guardianEmail}
                      placeholder="john@example.com"
                    />
                    <Input
                      label="Phone"
                      type="tel"
                      value={formData.guardianPhone}
                      onChange={(e) => setFormData(prev => ({ ...prev, guardianPhone: e.target.value }))}
                      error={errors.guardianPhone}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Route */}
              <Card>
                <CardHeader>
                  <CardTitle>Route Details</CardTitle>
                  <CardDescription>Enter your home and school addresses</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Pickup Address */}
                  <div className="relative">
                    <Input
                      label="Home Address (Pickup)"
                      value={formData.pickupAddress}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, pickupAddress: e.target.value }));
                        handleAddressSearch(e.target.value, 'pickup');
                      }}
                      onFocus={() => setShowPickupSuggestions(pickupSuggestions.length > 0)}
                      error={errors.pickupAddress}
                      placeholder="123 Main St, Philadelphia, PA"
                    />
                    {showPickupSuggestions && pickupSuggestions.length > 0 && (
                      <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                        {pickupSuggestions.map((suggestion) => (
                          <button
                            key={suggestion.place_id}
                            type="button"
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0"
                            onClick={() => selectAddress(suggestion.description, 'pickup')}
                          >
                            {suggestion.description}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Dropoff Address */}
                  <div className="relative">
                    <Input
                      label="School Address (Dropoff)"
                      value={formData.dropoffAddress}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, dropoffAddress: e.target.value }));
                        handleAddressSearch(e.target.value, 'dropoff');
                      }}
                      onFocus={() => setShowDropoffSuggestions(dropoffSuggestions.length > 0)}
                      error={errors.dropoffAddress}
                      placeholder="456 School Ave, Philadelphia, PA"
                    />
                    {showDropoffSuggestions && dropoffSuggestions.length > 0 && (
                      <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                        {dropoffSuggestions.map((suggestion) => (
                          <button
                            key={suggestion.place_id}
                            type="button"
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0"
                            onClick={() => selectAddress(suggestion.description, 'dropoff')}
                          >
                            {suggestion.description}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {errors.route && (
                    <p className="text-sm text-red-600">{errors.route}</p>
                  )}

                  <Button
                    type="button"
                    variant="outline"
                    onClick={calculateRouteAndPricing}
                    disabled={isCalculating || !formData.pickupAddress || !formData.dropoffAddress}
                    loading={isCalculating}
                    className="w-full"
                  >
                    {routeCalculation ? '↻ Recalculate Route' : '📍 Calculate Route & Pricing'}
                  </Button>

                  {routeCalculation && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-semibold text-green-900">Route Calculated</p>
                          <p className="text-sm text-green-700">
                            {routeCalculation.distanceMiles.toFixed(1)} miles • ~{routeCalculation.durationMinutes} min
                          </p>
                        </div>
                        <span className="text-2xl">✓</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Students */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle>Students</CardTitle>
                      <CardDescription>Add up to 6 students • More students = lower per-student rate</CardDescription>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addStudent}
                      disabled={formData.students.length >= 6}
                    >
                      + Add Student
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {errors.students && (
                    <p className="text-sm text-red-600">{errors.students}</p>
                  )}
                  {formData.students.map((student, index) => (
                    <div key={student.id} className="flex gap-3 items-end">
                      <div className="flex-1">
                        <Input
                          label={index === 0 ? 'First Name' : undefined}
                          value={student.firstName}
                          onChange={(e) => updateStudent(index, 'firstName', e.target.value)}
                          placeholder="First name"
                        />
                      </div>
                      <div className="flex-1">
                        <Input
                          label={index === 0 ? 'Last Name' : undefined}
                          value={student.lastName}
                          onChange={(e) => updateStudent(index, 'lastName', e.target.value)}
                          placeholder="Last name"
                        />
                      </div>
                      {formData.students.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeStudent(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          ✕
                        </Button>
                      )}
                    </div>
                  ))}

                  {/* Pricing tiers preview */}
                  {pricingTiers.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm font-medium text-gray-700 mb-2">Price per student per week:</p>
                      <div className="flex flex-wrap gap-2">
                        {pricingTiers.map((tier) => (
                          <span
                            key={tier.students}
                            className={`px-3 py-1 rounded-full text-sm ${
                              tier.students === formData.students.length
                                ? 'bg-shuttlebee-yellow text-gray-900 font-semibold'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {tier.students} student{tier.students > 1 ? 's' : ''}: {formatCurrency(tier.pricePerWeek / tier.students)}/ea
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Schedule */}
              <Card>
                <CardHeader>
                  <CardTitle>Schedule</CardTitle>
                  <CardDescription>Select your transportation schedule</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Semester Type */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {(Object.entries(SEMESTER_CONFIG) as [SemesterType, typeof SEMESTER_CONFIG.full][]).map(([key, config]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, semesterType: key }))}
                        className={`
                          p-3 rounded-lg border-2 text-center transition-all
                          ${formData.semesterType === key
                            ? 'border-shuttlebee-yellow bg-shuttlebee-yellow/10'
                            : 'border-gray-200 hover:border-gray-300'
                          }
                        `}
                      >
                        <p className="font-semibold text-sm">{config.label}</p>
                        <p className="text-xs text-gray-500">{config.weeks > 0 ? `${config.weeks} weeks` : 'Flexible'}</p>
                      </button>
                    ))}
                  </div>

                  {/* Days per week */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Days per Week
                    </label>
                    <div className="flex gap-2">
                      {[3, 4, 5].map((days) => (
                        <button
                          key={days}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, daysPerWeek: days }))}
                          className={`
                            flex-1 py-2 px-4 rounded-lg border-2 transition-all
                            ${formData.daysPerWeek === days
                              ? 'border-shuttlebee-yellow bg-shuttlebee-yellow/10 font-semibold'
                              : 'border-gray-200 hover:border-gray-300'
                            }
                          `}
                        >
                          {days} days
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Custom days */}
                  {formData.semesterType === 'custom' && (
                    <Input
                      label="Total School Days"
                      type="number"
                      value={formData.customDays}
                      onChange={(e) => setFormData(prev => ({ ...prev, customDays: parseInt(e.target.value) || 0 }))}
                      min={1}
                      max={200}
                    />
                  )}
                </CardContent>
              </Card>

              <Button
                type="submit"
                size="xl"
                variant="shuttlebee"
                className="w-full"
                disabled={isSubmitting || !routeCalculation}
                loading={isSubmitting}
              >
                Submit Quote Request
              </Button>
            </form>
          </div>

          {/* Price Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <Card className="border-2 border-shuttlebee-yellow">
                <CardHeader className="bg-shuttlebee-yellow/10">
                  <CardTitle className="text-center">Quote Summary</CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  {/* Route */}
                  {routeCalculation ? (
                    <div>
                      <p className="text-sm text-gray-500">Route</p>
                      <p className="font-medium text-sm">
                        {formData.pickupAddress.split(',')[0]} → {formData.dropoffAddress.split(',')[0]}
                      </p>
                      <p className="text-xs text-gray-500">
                        {routeCalculation.distanceMiles.toFixed(1)} mi • ~{routeCalculation.durationMinutes} min
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-400">
                      <p className="text-sm">Enter addresses and calculate route to see pricing</p>
                    </div>
                  )}

                  {selectedTier && (
                    <>
                      {/* Schedule */}
                      <div>
                        <p className="text-sm text-gray-500">Schedule</p>
                        <p className="font-medium">
                          {SEMESTER_CONFIG[formData.semesterType].label}
                        </p>
                        <p className="text-xs text-gray-500">
                          {getTotalWeeks()} weeks × {formData.daysPerWeek} days/week
                        </p>
                      </div>

                      {/* Students */}
                      <div>
                        <p className="text-sm text-gray-500">Students</p>
                        <p className="font-medium">{formData.students.length}</p>
                      </div>

                      <hr />

                      {/* Price Breakdown */}
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Weekly rate</span>
                          <span>{formatCurrency(selectedTier.pricePerWeek)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">× {getTotalWeeks()} weeks</span>
                          <span>{formatCurrency(calculateTotal())}</span>
                        </div>
                      </div>

                      <hr />

                      {/* Total */}
                      <div className="flex justify-between items-end">
                        <span className="font-semibold text-gray-900">Total</span>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-shuttlebee-gold">
                            {formatCurrency(calculateTotal())}
                          </p>
                          <p className="text-xs text-gray-500">
                            ~{formatCurrency(calculateTotal() / getTotalDays())}/day
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Back Button */}
              <Button
                type="button"
                variant="ghost"
                className="w-full mt-4"
                onClick={() => navigate('/')}
              >
                ← Back to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
